/**
 * "Would you consider locking money away for a guaranteed income for life?" — the client's answer,
 * read by every module's candidate generator.
 *
 * Owner, 24-25 September 2026. The village Suggestion Engine asked this in its Strategy Analysis
 * Setup and no other module did, so a client who had said no in one place was still shown annuity
 * strategies everywhere else. The willingness is a property of the CLIENT, not of the module, so it
 * is saved once on the client record and honoured by all of them.
 *
 * Asked in everyday words, never as "% into an annuity" — a client should not need to know what an
 * annuity is to answer. The wording lives in the screens; this holds the answer and the rules.
 */
import type { FactFind } from './factFindTypes.ts';
import { guaranteedShapeAvailable } from './carePlusSplit.ts';

/** Whether an annuity may be offered at all. */
export type AnnuityAppetite = 'none' | 'yes' | 'unsure';
/**
 * Which shapes to build when the answer is yes. 'both' is the default and the unsure case.
 *
 * 'guaranteed' (27 Sep 2026) is the annuity-plus-life-policy shape: the full amount to the estate
 * whenever death comes. It is a different product from a capital-returned plain annuity, whose
 * death benefit tapers on the capital access schedule, and a client who asks for "100% back" means
 * this one. Until it existed here, that client was silently given the tapering shape instead.
 */
export type AnnuityResidual = 'returned' | 'nil' | 'guaranteed' | 'both' | 'back' | 'all';

export interface AnnuityPreference {
    appetite: AnnuityAppetite;
    residual: AnnuityResidual;
    /** Whether the client has actually answered, as opposed to falling back to the default. */
    answered: boolean;
}

const APPETITES: AnnuityAppetite[] = ['none', 'yes', 'unsure'];
const RESIDUALS: AnnuityResidual[] = ['returned', 'nil', 'guaranteed', 'both', 'back', 'all'];

/*
 * INCOME ONLY IS TESTED ONLY WHEN THE CLIENT ASKS FOR IT (owner, 21 Oct 2026 — the guaranteed income review,
 * decision D1, and the agreed mock-up docs/ux-review/annuities/). The question has three answers:
 *   "Yes, if the money goes back to the family"        → appetite 'yes', residual 'back' (the suggested answer)
 *   "Yes, and the most income, even if nothing goes back" → appetite 'yes', residual 'all'
 *   "No, keep all the money within reach"              → appetite 'none'
 * 'back' builds the types that return the money (money back in the early years, and the full amount back at
 * any time where the client may buy it); 'all' adds income only (level and rising with prices). An unanswered
 * or "not sure" profile is treated as 'back': a plan never puts money where nothing comes back unless the
 * client asked. Earlier answers keep their meaning: 'nil' was an explicit ask for income only, 'returned' and
 * 'guaranteed' narrow to that one type, and 'both' (the old "whichever works best") reads as 'back'.
 */

/**
 * The saved answer, or the default: "not sure", which models both versions — the behaviour every
 * module had before the question existed, so an unanswered profile is never quietly narrowed.
 *
 * `goals.rvAnnuityAppetite` / `rvAnnuityResidual` are read as a fallback: the village asked first,
 * under its own field names, and a profile answered then must not lose its answer.
 */
export function annuityPreferenceOf(factFind: FactFind | null | undefined): AnnuityPreference {
    const goals = (factFind?.goals || {}) as Record<string, unknown>;
    const rawAppetite = goals.annuityAppetite ?? goals.rvAnnuityAppetite;
    const rawResidual = goals.annuityResidual ?? goals.rvAnnuityResidual;
    const appetite = APPETITES.includes(rawAppetite as AnnuityAppetite) ? (rawAppetite as AnnuityAppetite) : 'unsure';
    const stored = RESIDUALS.includes(rawResidual as AnnuityResidual) ? (rawResidual as AnnuityResidual) : 'back';
    // "Not sure", and the old "whichever works best", test the types that return the money.
    const residual: AnnuityResidual = appetite !== 'yes' || stored === 'both' ? 'back' : stored;
    return { appetite, residual, answered: APPETITES.includes(rawAppetite as AnnuityAppetite) };
}

/** Whether ANY annuity candidate may be generated. */
export function allowsAnnuity(preference: AnnuityPreference): boolean {
    return preference.appetite !== 'none';
}

/**
 * Whether a plain lifetime annuity with this residual share (0 to 1) may be generated.
 *
 * Income only (nothing returned) needs the client's explicit ask ('all', or the earlier 'nil'). Money back
 * is built for 'back', 'all' and 'returned'. A client who asked only for the full amount at any time
 * ('guaranteed') gets neither plain type.
 */
export function allowsResidual(preference: AnnuityPreference, residualCapital: number): boolean {
    if (!allowsAnnuity(preference)) return false;
    const returnsCapital = Number(residualCapital) > 0;
    switch (preference.residual) {
        case 'all': return true;
        case 'nil': return !returnsCapital;
        case 'returned':
        case 'back':
        case 'both': return returnsCapital;
        case 'guaranteed': return false;
        default: return returnsCapital;
    }
}

/** Whether the client asked for income only to be tested ("the most income, even if nothing goes back"). */
export function asksForIncomeOnly(preference: AnnuityPreference): boolean {
    return allowsAnnuity(preference) && (preference.residual === 'all' || preference.residual === 'nil');
}

/**
 * Whether the GUARANTEED shape may be generated for this client.
 *
 * Two conditions, both necessary. The client's answer must allow it — a straight ask for it, or
 * "show me whichever works best", or no answer yet — and the client must be someone the product is
 * sold to: receiving or entering government-subsidised aged care (`guaranteedShapeAvailable`). The
 * second is a rule about the person, not the module, so it is read from the fact-find here rather
 * than decided by whichever screen is asking.
 */
export function allowsGuaranteed(preference: AnnuityPreference, factFind: FactFind | null | undefined, owner: 'client' | 'partner' | 'joint' = 'client'): boolean {
    if (!allowsAnnuity(preference)) return false;
    const wanted = preference.residual === 'guaranteed' || preference.residual === 'back' || preference.residual === 'all' || preference.residual === 'both';
    return wanted && guaranteedShapeAvailable(factFind, owner);
}

/*
 * HOW MUCH, AT MOST (owner, 21 Oct 2026, decision D3 and the mock-up's share question): one ceiling for every
 * amount a tested plan puts into a guaranteed income, the client's own answer — 10%, 25% (suggested) or 50%
 * of the money that could be invested, or another amount in dollars or as a share. It is about keeping money
 * within reach, so it applies to every type, the full-amount-back type included. Asked once for the client
 * and dated; a module's own older share answer is read when the client-level one is absent.
 */
export const DEFAULT_ANNUITY_SHARE_PCT = 25;

export interface AnnuityCeiling {
    /** A share of the money that could be invested, 0–100; null when the client gave a dollar amount. */
    pct: number | null;
    /** A dollar ceiling, when the client gave one. */
    amount: number | null;
    /** The date the client answered (YYYY-MM-DD), for the record. */
    answeredOn: string | null;
    /** Whether the client answered, as opposed to the suggested 25%. */
    answered: boolean;
}

export function annuityCeilingOf(factFind: FactFind | null | undefined, moduleSharePct?: number | null): AnnuityCeiling {
    const goals = (factFind?.goals || {}) as Record<string, unknown>;
    const answeredOn = typeof goals.annuityAnsweredOn === 'string' && goals.annuityAnsweredOn ? String(goals.annuityAnsweredOn) : null;
    const amount = Number(goals.annuityShareCapAmount);
    if (Number.isFinite(amount) && amount > 0) return { pct: null, amount, answeredOn, answered: true };
    const pct = Number(goals.annuityShareCapPct);
    if (Number.isFinite(pct) && pct > 0) return { pct: Math.min(100, pct), amount: null, answeredOn, answered: true };
    const modulePct = Number(moduleSharePct);
    if (Number.isFinite(modulePct) && modulePct > 0) return { pct: Math.min(100, modulePct), amount: null, answeredOn, answered: true };
    return { pct: DEFAULT_ANNUITY_SHARE_PCT, amount: null, answeredOn, answered: false };
}

/** The ceiling in dollars, for the money a plan could invest. */
export function annuityCeilingAmount(ceiling: AnnuityCeiling, investable: number): number {
    const inv = Math.max(0, Number(investable) || 0);
    if (ceiling.amount != null) return Math.min(inv, ceiling.amount);
    return Math.round(inv * ((ceiling.pct ?? DEFAULT_ANNUITY_SHARE_PCT) / 100));
}

/** Convenience for a generator that holds the fact-find rather than a preference. */
export function annuityAllowedFor(factFind: FactFind | null | undefined, residualCapital: number): boolean {
    return allowsResidual(annuityPreferenceOf(factFind), residualCapital);
}
